import { ProjectResultMetrics } from './components/ProjectResultMetrics';
import { ProjectMapCard } from './components/ProjectMapCard';
import { useProjectListView } from '../../project-list-view.context';
import { useProjectLabels } from '../../labels/project-labels.hooks';
import { APP_HEADER_BACKGROUND_COLOR } from '@client/style/style.constants';
import {
  useProjectListMap,
  useProjectListMapSelectedProjectId,
} from '../../project-list-map.context';
import { useProjectNavigation } from '@client/hooks/use-router.hooks';
import { useProjectState } from '@client/store/project';
import { useNavigate } from 'react-router-dom';
import { Page } from '@client/store/ui/ui.model';
import { openProjectWithAnimation } from '../helpers/project-open-animation.helpers';
import { shouldShowProjectResultMetrics } from '../../helpers/project-result-metrics.helpers';
import {
  ProjectLabelPicker,
  ProjectLabelPills,
} from '../../labels/ProjectLabels';
import {
  PROJECT_LIST_ROW_HEIGHT,
  PROJECT_LIST_SIDE_GUTTER,
  PROJECT_MAP_CARD_GAP,
  PROJECT_SELECTED_BACKGROUND,
  PROJECT_HOVER_BACKGROUND,
  PROJECT_SELECTED_SHADOW,
  PROJECT_SURFACE_RADIUS,
} from '../project-list.constants';
import { ELEMENT_LIST_HEADER_HEIGHT } from '@features/proposals/ElementListRow/element-list.constants';
import ElementListRow from '@features/proposals/ElementListRow/ElementListRow';
import DateText from '@ui/DateText';
import { GhostButton } from '@ui/GhostButton';

import amplitudeLog from '@client/amplitude';
import {
  AMP_PROJECT_EXPORT_BOVERKET_XML,
  AMP_PROJECT_EXPORT_SPREADSHEET,
  AMP_PROJECT_SAVE_AS_TEMPLATE,
  AMP_PROJECT_SELECT,
} from '@shared/constants';
import { useMouseEventCallback } from '@client/hooks/use-events.hooks';
import {
  useIsElementExpanded,
  useToggleElementExpanded,
} from '@client/hooks/use-expand-elements.hooks';
import {
  closeMenu,
  useMenuPosition,
  useTriggerContextMenu,
} from '@client/hooks/use-menu.hooks';
import {
  useProjectListResultsByScope,
  useResultsPerGFA,
} from '@features/results/use-results.hooks';
import { useBooleanState } from '@client/hooks/use-utils.hooks';
import { useIsSelected, useUIState } from '@client/store/ui';
import { Box, Tooltip } from '@mui/material';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { isDeactivated } from '@shared/helpers/element.helpers';
import { isProjectFolder } from '@shared/helpers/project-folder.helpers';
import { IProjectFolder } from '@shared/models/folder.model';
import { IProjectInfo } from '@shared/models/project.model';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ExpandButton } from '@ui/ElementList/Buttons/ExpandButton';
import {
  CONTENT_CELL_WIDTH,
  ROOT_CELL_WIDTH,
  Row,
  RowCell,
} from '@ui/ElementList';
import ProjectListItemBarCharts from './components/ProjectListItemBarCharts';
import ProjectListItemNameInput from './components/ProjectListItemNameInput';
import RestrictProjectButton from '../../RestrictProjectButton';
import ProjectOwnerMenu from '../../ProjectOwnerMenu';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';
import { useExportSpreadsheet } from '@client/hooks/use-export-spreadsheet.hook';
import { useExportBoverketXml } from '@client/hooks/use-export-boverket-xml.hook';
import { useUserIsOwner } from '@client/hooks/use-user.hooks';
import { useProjectRestriction } from '@client/store/project';
import { Menu } from '@ui/menus/Menu';
import { useEditProjectDialogStore } from '@features/projects/EditProject/edit-project.store';
import { useErrorHandler } from '@client/hooks/use-error-handler.hooks';
import { validateFiniteNumber } from '@shared/validation/number.validation';
import { is } from '@client/helpers/dom.guards';
import { guards } from '@shared/helpers/typeguards.helpers';
import { ProjectMenuContent } from './components/ProjectMenuContent';
import { useProjectAction } from './use-project-action.hook';
import {
  getProjectListBarCellSx,
  getProjectListColumnHideSx,
  PROJECT_LIST_ROW_SX,
  PROJECT_LIST_CONTAINER_NAME,
  getProjectListColumnHideMaxWidth,
} from '../helpers/project-list-columns.helpers';

type ProjectListItemProps = {
  item: IProjectInfo | IProjectFolder;
  indentation?: number;
  autoFocus?: boolean;
};

const ProjectListItemComponent: React.FC<ProjectListItemProps> = ({
  item,
  indentation = 0,
  autoFocus,
}) => {
  const { setAddedElementId, setSelectedProjectFolderId } = useUIState(
    'setAddedElementId',
    'setSelectedProjectFolderId',
  );
  const { id, gfa = 0 } = item;
  const previewProject = useProjectListMap();
  const { view } = useProjectListView();
  const navigateTo = useProjectNavigation();
  const navigate = useNavigate();
  const { fetchProject } = useProjectState('fetchProject');
  const { assignments } = useProjectLabels();
  const hasLabels = !!assignments[id]?.length;

  const toggleElementExpanded = useToggleElementExpanded(item);
  const expanded = useIsElementExpanded(item);
  const triggerContextMenu = useTriggerContextMenu(id);

  const displayCurrency = useDisplayCurrency();
  const folderSelected = useIsSelected(item);
  const selectedProjectId = useProjectListMapSelectedProjectId();
  const selected = isProjectFolder(item)
    ? folderSelected
    : selectedProjectId === item.id;
  const deactivated = isDeactivated(item);
  const isFolder = isProjectFolder(item);

  const filteredResults = useProjectListResultsByScope(item.results);

  const resultsPerGFA = useResultsPerGFA(filteredResults, item.gfa ?? 0);
  const baselineResults = useProjectListResultsByScope(
    isFolder ? undefined : item.baseline_results,
  );
  const baselinePerGFA = useResultsPerGFA(baselineResults, item.gfa ?? 0);
  const costPerGFA = getConversionFactorValue(resultsPerGFA, 'sek');
  const co2ePerGFA = getConversionFactorValue(resultsPerGFA, 'co2e');
  const co2Diff =
    !isFolder && item.results && item.baseline_results && gfa > 0
      ? co2ePerGFA - getConversionFactorValue(baselinePerGFA, 'co2e')
      : undefined;
  const displayCost = getDisplayValueData(
    { value: costPerGFA, unit: 'sek' },
    {
      outputUnit: displayCurrency.isEnabled ? displayCurrency.current : 'SEK',
      notation: 'compact',
    },
  );
  const [hover, startHover, endHover] = useBooleanState(false);

  const [isEditingName, startEditingName, stopEditingName] =
    useBooleanState(false);

  const [kebabMenuAnchor, setKebabMenuAnchor] = useState<HTMLElement | null>(
    null,
  );

  const [labelsAnchor, setLabelsAnchor] = useState<HTMLElement | null>(null);
  const rowRef = useRef<HTMLDivElement>(null);

  const restrictProject = useProjectRestriction();
  const exportSpreadsheet = useExportSpreadsheet();
  const exportBoverketXml = useExportBoverketXml();
  const errorHandler = useErrorHandler();
  const setEditProjectDialog = useEditProjectDialogStore((store) => store.set);
  const menuPosition = useMenuPosition(id);

  const numericProjectId = useMemo(() => {
    if (isFolder) return undefined;
    const parsed = Number(id);
    if (!Number.isFinite(parsed)) throw new Error('Invalid project id');
    return parsed;
  }, [id, isFolder]);

  const isOwner = useUserIsOwner(numericProjectId ?? 0);
  const isMenuOpen = useMemo(
    () => guards.is.defined(kebabMenuAnchor || menuPosition),
    [kebabMenuAnchor, menuPosition],
  );

  const projectAction = useProjectAction(numericProjectId);

  const handleFolderClick = useMouseEventCallback(
    (event) => {
      if (
        is.Element(event.target) &&
        event.target.closest('button, a, [role="button"]')
      )
        return;
      if (isFolder) {
        setSelectedProjectFolderId(selected ? undefined : item.id);
      } else if (!isEditingName) {
        previewProject(item.id);
      }
    },
    { ignoreInputEvents: true, ignoreOverlayEvents: true },
  );

  const openProject = useCallback(
    async (source: Element) => {
      amplitudeLog(AMP_PROJECT_SELECT, { ProjectId: id });
      // Include versionId so list→Design uses the strict `/v/:versionId/design`
      // path; omitting it navigates to a partial URL that never upgrades.
      const versionId =
        !isFolder && 'active_version_id' in item
          ? (item.active_version_id ?? item.versionIds[0])
          : undefined;
      await openProjectWithAnimation(
        source,
        (cameraReady) => fetchProject(Number(id), undefined, cameraReady),
        () =>
          navigateTo(
            { projectId: id, versionId, page: Page.Design },
            { flushSync: true },
          ),
        Number(id),
        (url) => navigate(url, { flushSync: true }),
      );
    },
    [id, item, isFolder, navigateTo, navigate, fetchProject],
  );

  const handleOpenProject = useMouseEventCallback(
    (event) => openProject(event.currentTarget),
    { stopPropagation: true },
  );

  const handleRowDoubleClick = useMouseEventCallback(
    async (event) => {
      if (isFolder) {
        toggleElementExpanded();
        return;
      }
      if (
        isEditingName ||
        (is.Element(event.target) &&
          event.target.closest('button, a, [role="button"]'))
      )
        return;
      await openProject(event.currentTarget);
    },
    { ignoreInputEvents: true, ignoreOverlayEvents: true },
  );

  const handleMenuClose = useCallback(() => {
    closeMenu();
    setKebabMenuAnchor(null);
    endHover();
  }, [endHover]);

  const handleKebabOpen = useMouseEventCallback(
    (event) => {
      closeMenu();
      setKebabMenuAnchor(
        is.HTMLElement(event.currentTarget) ? event.currentTarget : null,
      );
    },
    { stopPropagation: true },
  );

  const handleEditProject = projectAction((validProjectId) =>
    setEditProjectDialog({
      type: 'project',
      mode: 'edit',
      id: validProjectId,
    }),
  );

  const handleDuplicateProject = projectAction(
    (validProjectId) =>
      setEditProjectDialog({
        type: 'project',
        mode: 'duplicate',
        id: validProjectId,
      }),
    'duplicate-project',
  );

  const handleLockProject = projectAction((validProjectId) =>
    restrictProject(validateFiniteNumber(validProjectId), item.name, {
      lock: true,
    }),
  );

  const handleUnlockProject = projectAction((validProjectId) =>
    restrictProject(validateFiniteNumber(validProjectId), item.name, {
      lock: false,
    }),
  );

  const handleArchiveProject = projectAction(
    (validProjectId) =>
      restrictProject(
        validateFiniteNumber(validProjectId),
        item.name,
        { archive: true, lock: false },
        true,
      ),
    'archive-project',
  );

  const handleUnarchiveProject = projectAction(
    (validProjectId) =>
      restrictProject(
        validateFiniteNumber(validProjectId),
        item.name,
        { archive: false },
        true,
      ),
    'unarchive-project',
  );

  const handleSaveAsTemplate = projectAction((validProjectId) => {
    amplitudeLog(AMP_PROJECT_SAVE_AS_TEMPLATE, {
      ProjectId: validProjectId,
    });
    setEditProjectDialog({
      type: 'template',
      mode: 'create',
      id: validProjectId,
    });
  }, 'create-template-project');

  const handleExportSpreadsheet = projectAction((validProjectId) => {
    amplitudeLog(AMP_PROJECT_EXPORT_SPREADSHEET, {
      ProjectId: validProjectId,
    });
    void errorHandler(() => exportSpreadsheet(validProjectId));
  }, 'export-spreadsheet');

  const handleExportBoverketXml = projectAction((validProjectId) => {
    amplitudeLog(AMP_PROJECT_EXPORT_BOVERKET_XML, {
      ProjectId: validProjectId,
    });
    void errorHandler(() => exportBoverketXml(validProjectId));
    // Same pro gate as spreadsheet export until a dedicated paywall feature exists.
  }, 'export-spreadsheet');

  useEffect(() => {
    if (autoFocus) {
      if (isFolder) {
        setSelectedProjectFolderId(item.id);
        setAddedElementId(undefined);
        return;
      }
      startEditingName();
    }
  }, [
    autoFocus,
    isFolder,
    item,
    setAddedElementId,
    setSelectedProjectFolderId,
    startEditingName,
  ]);

  const showResultMetrics = shouldShowProjectResultMetrics(
    co2ePerGFA,
    costPerGFA,
  );
  const resultMetrics = !isFolder && showResultMetrics && (
    <ProjectResultMetrics
      co2={getDisplayNumber(co2ePerGFA, { notation: 'compact' })}
      co2Diff={co2Diff}
      cost={displayCost.value}
      currency={displayCurrency.isEnabled ? displayCurrency.current : undefined}
      selected={selected}
    />
  );

  return (
    <Box
      ref={rowRef}
      onMouseEnter={startHover}
      onMouseLeave={endHover}
      onDoubleClick={handleRowDoubleClick}
      sx={{
        width: '100%',
        position: 'relative',
        ...(view === 'Map' && {
          scrollMarginTop: `${ELEMENT_LIST_HEADER_HEIGHT + 24}px`,
          scrollMarginBottom: '24px',
        }),
        pointerEvents: 'auto',
        opacity: deactivated ? 0.5 : 1,
        '& .row-hover:hover, & .row-hover[data-project-opening-hover]': {
          backgroundColor:
            view === 'List'
              ? PROJECT_HOVER_BACKGROUND
              : APP_HEADER_BACKGROUND_COLOR,
        },
        '& .row-hover:hover::after, & .row-hover[data-project-opening-hover]::after':
          { backgroundColor: 'transparent' },
        ...(view === 'List' && {
          boxSizing: 'border-box',
          padding: `0 ${PROJECT_LIST_SIDE_GUTTER}px ${PROJECT_MAP_CARD_GAP}px`,
          '--element-list-selected-background': 'transparent',
          '--element-list-selected-border-color': 'transparent',
          '& .row-hover': {
            borderRadius: PROJECT_SURFACE_RADIUS,
            backgroundColor:
              isMenuOpen && !selected
                ? PROJECT_HOVER_BACKGROUND
                : 'transparent',
          },
          '& .row-hover::after': {
            borderRadius: 'inherit',
            backgroundColor: 'transparent',
          },
          '& .row-hover[aria-selected="true"], & .row-hover[aria-selected="true"]:hover':
            {
              backgroundColor: PROJECT_SELECTED_BACKGROUND,
              boxShadow: PROJECT_SELECTED_SHADOW,
            },
        }),
      }}
    >
      {view === 'Map' && !isFolder ? (
        <ProjectMapCard
          project={item}
          onOpen={handleOpenProject}
          labels={
            <ProjectLabelPills projectId={id} onClick={setLabelsAnchor} />
          }
          name={
            <ProjectListItemNameInput
              item={item}
              autoFocus={autoFocus}
              isEditingName={isEditingName}
              onCancel={stopEditingName}
              onClick={handleFolderClick}
              fontSize={18}
              color="rgba(0,0,0,calc(0.8 + var(--project-card-text-boost, 0)))"
            />
          }
          owner={
            item.archived || item.locked ? (
              <RestrictProjectButton hover={hover} project={item} />
            ) : (
              <ProjectOwnerMenu projectInfo={item} onClose={endHover} />
            )
          }
          hideResultMetrics={!showResultMetrics}
          co2={getDisplayNumber(co2ePerGFA, { notation: 'compact' })}
          co2Diff={co2Diff}
          cost={displayCost.value}
          currency={
            displayCurrency.isEnabled ? displayCurrency.current : undefined
          }
          onClick={handleFolderClick}
          onContextMenu={triggerContextMenu}
          onMenuClick={handleKebabOpen}
          menuOpen={isMenuOpen}
        />
      ) : (
        <ElementListRow
          hideSurfaceBackground
          height={PROJECT_LIST_ROW_HEIGHT}
          name={item.name}
          variant={isFolder ? 'group' : 'element'}
          expandable={isFolder}
          expanded={expanded}
          onExpandedChange={toggleElementExpanded}
          groupLeadingSlot={
            isFolder ? <ExpandButton element={item} /> : undefined
          }
          depth={indentation + 1}
          selected={selected}
          onSelect={handleFolderClick}
          onContextMenu={triggerContextMenu}
          onKebabClick={handleKebabOpen}
          kebabMenuOpen={isMenuOpen}
          quantityValue={String(gfa)}
          quantityDisplayValue={getDisplayNumber(gfa)}
          quantityReadonly
          quantityFontWeight={500}
          quantityFontSize={12}
          quantityColor="rgba(0,0,0,0.5)"
          quantityTooltip="Gross floor area"
          unit="m²"
          iconSlot={isFolder ? <ExpandButton element={item} /> : null}
          nameLeadingSlot={
            !isFolder ? (
              <Box
                sx={{
                  width: 26,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}
                onClick={(event) => event.stopPropagation()}
              >
                {item.archived || item.locked ? (
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <RestrictProjectButton hover={hover} project={item} />
                  </Box>
                ) : (
                  <ProjectOwnerMenu projectInfo={item} onClose={endHover} />
                )}
              </Box>
            ) : undefined
          }
          nameOverrideSlot={
            <ProjectListItemNameInput
              item={item}
              contentSized={hasLabels}
              autoFocus={autoFocus}
              isEditingName={isEditingName}
              onCancel={stopEditingName}
              onClick={handleFolderClick}
            />
          }
          nameTrailingSlot={
            !isFolder ? (
              <ProjectLabelPills projectId={id} onClick={setLabelsAnchor} />
            ) : undefined
          }
          trailingSlotPaddingLeft={0}
          trailingSlot={
            view === 'Map' ? (
              <></>
            ) : (
              <Row
                height={PROJECT_LIST_ROW_HEIGHT}
                spacing={0}
                sx={{
                  ...PROJECT_LIST_ROW_SX,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <RowCell
                  width={ROOT_CELL_WIDTH.BAR}
                  responsiveWidth={1}
                  sx={{ ...getProjectListBarCellSx(), overflow: 'visible' }}
                >
                  {!isFolder && (
                    <ProjectListItemBarCharts
                      projectId={id}
                      gfa={gfa}
                      results={item.results}
                      programAreas={item.program_areas}
                      trailingMetrics={resultMetrics}
                    />
                  )}
                </RowCell>
                <RowCell
                  width={162}
                  sx={{
                    flexBasis: 'auto',
                    marginLeft: 'auto',
                    paddingLeft: '12px',
                    visibility: 'hidden',
                    [`@container ${PROJECT_LIST_CONTAINER_NAME} (max-width: ${getProjectListColumnHideMaxWidth('bars')}px)`]:
                      {
                        visibility: 'visible',
                      },
                  }}
                >
                  {!isFolder && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                      }}
                    >
                      {resultMetrics}
                    </Box>
                  )}
                </RowCell>
                <RowCell
                  width={CONTENT_CELL_WIDTH.DATE}
                  align="right"
                  paddingRight="16px"
                  data-project-updated-cell=""
                  sx={{
                    ...getProjectListColumnHideSx('updatedAt'),
                    alignSelf: 'stretch',
                  }}
                >
                  <Tooltip title="Last updated" disableInteractive>
                    <Box
                      component="span"
                      data-project-updated-value=""
                      sx={{
                        display: 'flex',
                        opacity: selected && !isFolder ? 0 : 1,
                        visibility:
                          hover && !(selected && !isFolder)
                            ? 'visible'
                            : 'hidden',
                      }}
                    >
                      <DateText
                        date={item.updated_at}
                        format={{ type: 'RELATIVE', dateFormat: 'd MMM' }}
                        sx={{
                          fontSize: 11,
                          textAlign: 'right',
                          whiteSpace: 'nowrap',
                          color: 'rgba(0,0,0,0.3)',
                          '&:hover': { color: 'rgba(0,0,0,0.8)' },
                        }}
                      />
                    </Box>
                  </Tooltip>
                </RowCell>
              </Row>
            )
          }
        />
      )}
      {view === 'List' && selected && !isFolder && (
        <Box
          sx={{
            position: 'absolute',
            right: PROJECT_LIST_SIDE_GUTTER + 13,
            top: 0,
            height: PROJECT_LIST_ROW_HEIGHT,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <GhostButton
            variant="primary"
            aria-label={`Open ${item.name}`}
            onClick={handleOpenProject}
          >
            Open
          </GhostButton>
        </Box>
      )}
      <Menu
        open={isMenuOpen}
        anchorEl={kebabMenuAnchor}
        preferredVerticalSide={view === 'Map' ? 'top' : undefined}
        anchorPosition={menuPosition}
        onClose={handleMenuClose}
      >
        {!isFolder && (
          <ProjectMenuContent
            projectId={id}
            locked={!!item.locked}
            archived={!!item.archived}
            isOwner={isOwner}
            actions={{
              edit: handleEditProject,
              duplicate: handleDuplicateProject,
              lock: handleLockProject,
              unlock: handleUnlockProject,
              archive: handleArchiveProject,
              unarchive: handleUnarchiveProject,
              template: handleSaveAsTemplate,
              exportSpreadsheet: handleExportSpreadsheet,
              exportBoverketXml: handleExportBoverketXml,
              updateProducts: undefined,
            }}
          />
        )}
      </Menu>
      {!isFolder && (
        <ProjectLabelPicker
          projectId={id}
          readonly={
            isOwner
              ? !!item.archived && !item.locked
              : !!item.locked || !!item.archived
          }
          anchor={labelsAnchor}
          onClose={() => setLabelsAnchor(null)}
        />
      )}
    </Box>
  );
};

export const ProjectListItem = memo(ProjectListItemComponent);
